import { useEffect, useState } from 'react';

export const useTruncateText = (
  containerWidth: number,
  originalText: string,
  endCharsCount: number = 3,
  customContainerWidth?: number,
  customFontSize?: string,
  setFullTextWidth?: (value: number) => void,
): { truncatedText: string; shouldTruncate: boolean } => {
  const [truncatedText, setTruncatedText] = useState<string>(originalText);
  const [shouldTruncate, setShouldTruncate] = useState<boolean>(false);

  useEffect(() => {
    const adjustedWidth = customContainerWidth || containerWidth;

    if (adjustedWidth <= 0) return;

    const truncateText = (text: string) => {
      const span = document.createElement('span');
      span.style.visibility = 'hidden';
      span.style.position = 'absolute';
      span.style.whiteSpace = 'nowrap';
      if (customFontSize) span.style.fontSize = customFontSize;
      span.textContent = text;
      document.body.append(span);
      const textWidth = span.offsetWidth;
      span.remove();
      if (setFullTextWidth) setFullTextWidth(textWidth);
      if (textWidth <= adjustedWidth) {
        return text;
      }

      let approximateFrontCharsCount = Math.floor((text.length * adjustedWidth) / textWidth);
      approximateFrontCharsCount = Math.max(0, approximateFrontCharsCount - endCharsCount * 2);

      return `${text.slice(0, approximateFrontCharsCount)}...${text.slice(-endCharsCount)}`;
    };

    const newText = truncateText(originalText);

    setTruncatedText(newText);

    setShouldTruncate(originalText !== newText);
  }, [containerWidth, originalText, endCharsCount, customContainerWidth, customFontSize]);

  return { truncatedText, shouldTruncate };
};

export const useTruncateLine = (ref: React.RefObject<HTMLElement>, lineClamp: number) => {
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (ref.current) {
      const lineHeight = Number.parseInt(getComputedStyle(ref.current).getPropertyValue('line-height'), 10);
      const lines = Math.ceil(ref.current.scrollHeight / lineHeight);
      const isOverflowing = lines > lineClamp;

      if (isOverflowing && ref.current.textContent) {
        const endIndex = ref.current.textContent.lastIndexOf(' ', (ref.current.textContent.length / lines) * lineClamp);
        ref.current.textContent = `${ref.current.textContent.slice(0, endIndex)}...`;
      }

      setIsTruncated(isOverflowing);
    }
  }, [ref, lineClamp]);

  return isTruncated;
};
